/**
 * Integrytas Group - Common Security Module (CSM)
 * useAuthSession - React hook that owns the AuthSession lifecycle.
 *
 * Responsibilities:
 *  - Subscribe to Supabase auth state.
 *  - Hydrate UserContext via auth_loader on sign-in / token refresh.
 *  - Expose signIn / signUp / signOut / changePassword / requestPasswordReset.
 *  - Expose checkFirstRun (anon-callable RPC) for the bootstrap UI.
 *  - Expose beginMimic / endMimic which write csm_user_mimic_log forensic rows
 *    and rebuild the effective UserContext for the impersonated user.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthSession } from '../types/auth';
import { supabase } from './supabase_client';
import { apiDb } from './api_client';
import { loadAuthSession } from './auth_loader';
import {
  setRememberMe,
  installRememberMeUnloadGuard,
  clearRememberMe,
} from './remember_me';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export interface SignUpInput {
  email: string;
  password: string;
  display_name?: string;
  preferred_locale?: string;
}

export interface UseAuthSessionResult {
  status: AuthStatus;
  session: AuthSession | null;
  error: string | null;

  /** TRUE while the user is in a Supabase PASSWORD_RECOVERY state - i.e.
   *  they followed the email reset link and must set a new password before
   *  the rest of the app is unlocked. */
  isPasswordRecovery: boolean;

  signIn:                (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  devLogin:              () => Promise<void>;
  signUp:                (input: SignUpInput) => Promise<{ needsEmailConfirmation: boolean }>;
  signOut:               () => Promise<void>;
  changePassword:        (newPassword: string) => Promise<void>;
  requestPasswordReset:  (email: string) => Promise<void>;
  /** Called after the user submits a new password during recovery; clears
   *  the recovery flag and rehydrates the regular session. */
  completePasswordRecovery: (newPassword: string) => Promise<void>;
  checkFirstRun:         () => Promise<boolean>;

  beginMimic: (mimickedUserId: number, businessReason: string) => Promise<void>;
  endMimic:   () => Promise<void>;
}

export function useAuthSession(): UseAuthSessionResult {
  const [status, setStatus]   = useState<AuthStatus>('loading');
  const [session, setSession] = useState<AuthSession | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  // The real admin id never resets across mimic toggles.
  const realAdminIdRef = useRef<number | null>(null);

  // Install the no-remember unload wipe exactly once.
  useEffect(() => { installRememberMeUnloadGuard(); }, []);

  // ---------------------------------------------------------------------------
  // Hydration
  // ---------------------------------------------------------------------------
  // Tracks whether we currently hold an authenticated session so background
  // re-hydrations (focus-triggered TOKEN_REFRESHED / SIGNED_IN events) can
  // skip the 'loading' splash. Flipping to 'loading' unmounts the whole
  // shell, which resets every open workspace tab — see hydrateFromSupabase.
  const hasSessionRef = useRef(false);

  const hydrateFromSupabase = useCallback(async () => {
    try {
      // Only show the blocking splash when we don't already have a session
      // (first boot, sign-in, sign-out). A silent refresh keeps the shell
      // mounted and swaps the session state underneath it.
      if (!hasSessionRef.current) setStatus('loading');
      setError(null);

      const { data, error: getErr } = await supabase.auth.getSession();
      if (getErr) throw getErr;

      const supaSession = data.session;
      if (!supaSession || !supaSession.user.email) {
        realAdminIdRef.current = null;
        hasSessionRef.current = false;
        setSession(null);
        setStatus('unauthenticated');
        return;
      }

      const csm = await loadAuthSession({
        authUser: supaSession.user,
        sessionToken: supaSession.access_token,
        expiresAt: new Date((supaSession.expires_at ?? 0) * 1000).toISOString(),
      });

      realAdminIdRef.current = csm.effective_user.user_id;
      hasSessionRef.current = true;
      setSession(csm);
      setStatus('authenticated');
    } catch (e) {
      // A failed SILENT refresh (transient network blip while already signed
      // in) must not tear down the shell — keep the current session and let
      // the next refresh retry. Only surface the error state when we have no
      // session to fall back on.
      if (hasSessionRef.current) {
        console.warn('Silent session refresh failed; keeping current session.', e);
        return;
      }
      setError(e instanceof Error ? e.message : (typeof (e as { message?: unknown })?.message === 'string' ? (e as { message: string }).message : String(e)));
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void hydrateFromSupabase();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      // Supabase emits PASSWORD_RECOVERY once it consumes the recovery
      // token from the URL fragment. The user is "signed in" but only with
      // recovery scope - we must show the set-new-password screen first.
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      }
      void hydrateFromSupabase();
    });
    return () => { sub.subscription.unsubscribe(); };
  }, [hydrateFromSupabase]);

  // ---------------------------------------------------------------------------
  // Auth actions
  // ---------------------------------------------------------------------------
  const signIn = useCallback(async (email: string, password: string, rememberMe = false) => {
    setError(null);
    setRememberMe(rememberMe);
    const { error: e } = await supabase.auth.signInWithPassword({ email, password });
    if (e) {
      const raw = e.message ?? '';
      // Supabase-js wraps browser network errors in AuthRetryableFetchError with the
      // raw browser message. Normalise to a user-readable message so the sign-in screen
      // shows something actionable instead of "Load failed" or "Failed to fetch".
      const isNetworkError =
        e.name === 'AuthRetryableFetchError' ||
        raw === 'Load failed' ||
        raw === 'Failed to fetch' ||
        raw.startsWith('NetworkError') ||
        raw.toLowerCase().includes('network connection was lost');
      setError(isNetworkError
        ? 'Cannot connect to the authentication server. Please ensure the backend is running and try again.'
        : raw);
      throw e;
    }
  }, []);

  // DEV-ONLY quick login: mint a session for the first super-admin with no
  // password. The gateway gates this on CSM.SECURITY.DEV_LOGIN_ENABLED (set only
  // by the dev seed), and the sign-in screen only offers it under import.meta.env.DEV.
  const devLogin = useCallback(async () => {
    setError(null);
    const { data, error: e } = await apiDb.devLogin();
    if (e || !data?.otp || !data?.email) {
      const msg = e?.message ?? 'Dev login is not available.';
      setError(msg);
      throw new Error(msg);
    }
    const { error: vErr } = await supabase.auth.verifyOtp({
      email: data.email, token: data.otp, type: 'email',
    });
    if (vErr) { setError(vErr.message); throw vErr; }
  }, []);

  const signUp = useCallback(async (input: SignUpInput) => {
    setError(null);
    const { data, error: e } = await supabase.auth.signUp({
      email: input.email,
      password: input.password,
      options: {
        data: {
          display_name:     input.display_name     ?? input.email.split('@')[0],
          preferred_locale: input.preferred_locale ?? 'EN_US',
        },
      },
    });
    if (e) { setError(e.message); throw e; }

    // If the project requires email confirmation, Supabase returns a user but
    // no session. The caller can use this flag to tell the user to check mail.
    return { needsEmailConfirmation: !data.session };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    clearRememberMe();
    realAdminIdRef.current = null;
    setSession(null);
    setStatus('unauthenticated');
  }, []);

  const changePassword = useCallback(async (newPassword: string) => {
    const { error: e } = await supabase.auth.updateUser({ password: newPassword });
    if (e) { setError(e.message); throw e; }
  }, []);

  /**
   * Sets the new password during a recovery flow, then exits recovery mode
   * by clearing isPasswordRecovery. The Supabase session remains valid, so
   * the regular hydrateFromSupabase() picks up where it left off and the
   * caller lands in the authenticated shell.
   */
  const completePasswordRecovery = useCallback(async (newPassword: string) => {
    const { error: e } = await supabase.auth.updateUser({ password: newPassword });
    if (e) { setError(e.message); throw e; }
    setIsPasswordRecovery(false);
    // Strip the recovery hash so a refresh doesn't re-enter the flow.
    if (typeof window !== 'undefined') {
      try { window.history.replaceState({}, '', window.location.pathname + window.location.search); }
      catch { /* noop */ }
    }
    await hydrateFromSupabase();
  }, [hydrateFromSupabase]);

  const requestPasswordReset = useCallback(async (email: string) => {
    setError(null);
    const { error: e } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/?flow=reset`,
    });
    if (e) { setError(e.message); throw e; }
  }, []);

  const checkFirstRun = useCallback(async (): Promise<boolean> => {
    try {
      const { data, error: e } = await apiDb.rpc('csm_is_first_run');
      if (e) return false;
      return Boolean(data);
    } catch {
      return false;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Mimic
  // ---------------------------------------------------------------------------
  const beginMimic = useCallback(async (mimickedUserId: number, businessReason: string) => {
    if (!session) throw new Error('CSM.AUTH.NO_SESSION');
    if (session.mimic.is_active) throw new Error('CSM.AUTH.MIMIC_ALREADY_ACTIVE');
    const realAdminId = realAdminIdRef.current ?? session.real_admin_id;

    const { data: logRow, error: logErr } = await apiDb
      .from('csm_user_mimic_log')
      .insert({
        real_admin_id:    realAdminId,
        mimicked_user_id: mimickedUserId,
        session_token:    session.session_token,
        business_reason:  businessReason,
        modified_by:      realAdminId,
      })
      .select('user_mimic_log_id, started_at')
      .single();
    if (logErr) throw logErr;

    const { data: targetUser, error: targetErr } = await apiDb
      .from('csm_user_master')
      .select('email')
      .eq('user_id', mimickedUserId)
      .single();
    if (targetErr) throw targetErr;

    const fakeAuthUser = { email: targetUser.email } as Parameters<typeof loadAuthSession>[0]['authUser'];
    const rebuilt = await loadAuthSession({
      authUser: fakeAuthUser,
      sessionToken: session.session_token,
      expiresAt: session.expires_at,
    });

    setSession({
      ...rebuilt,
      real_admin_id: realAdminId,
      mimic: {
        is_active: true,
        user_mimic_log_id: logRow.user_mimic_log_id,
        real_admin_id: realAdminId,
        mimicked_user_id: mimickedUserId,
        started_at: logRow.started_at,
        business_reason: businessReason,
      },
    });
  }, [session]);

  const endMimic = useCallback(async () => {
    if (!session || !session.mimic.is_active || !session.mimic.user_mimic_log_id) return;
    const { error: e } = await apiDb
      .from('csm_user_mimic_log')
      .update({ ended_at: new Date().toISOString(), modified_by: session.real_admin_id })
      .eq('user_mimic_log_id', session.mimic.user_mimic_log_id);
    if (e) throw e;
    await hydrateFromSupabase();
  }, [session, hydrateFromSupabase]);

  return {
    status, session, error, isPasswordRecovery,
    signIn, devLogin, signUp, signOut, changePassword, requestPasswordReset,
    completePasswordRecovery, checkFirstRun,
    beginMimic, endMimic,
  };
}
